import { Component, Input, Inject, OnDestroy, OnInit } from '@angular/core';
import cloneDeep from 'lodash-es/cloneDeep';
import sumBy from 'lodash-es/sumBy';

import { AllFiltersBase } from './all-filters-base.class';
import { DetectBrowser, DETECT_BROWSER } from '@finder/shared/utils/browser/detect-browser';
// eslint-disable-next-line max-len
import { FilterGroupModalComponent } from '@finder/features/list/components/filters/components/filter-group-modal/filter-group-modal.component';
import { FilterGroup } from '@finder/features/list/components/filters/classes/filter-group.class';
import { FilterEmitterService } from '@finder/shared/services/filter-emitter-service/filter-emitter.service';
import { HeightSliderService } from '@finder/features/list/components/filters/components/height-slider/height-slider.service';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { CommonModule } from '@angular/common';
import { IconTooltipComponent } from '@finder/shared/components/icon-tooltip/icon-tooltip.component';
import { RaCheckboxComponent } from '@finder/shared/components/ra-wrappers/ra-checkbox/ra-checkbox.component';
import { ButtonComponent } from '@finder/shared/components/button/button.component';
import { HeightSliderComponent } from '../height-slider/height-slider.component';

@Component({
    selector: 'finder-all-filters',
    templateUrl: './all-filters.component.html',
    styleUrls: ['./all-filters.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        IconTooltipComponent,
        RaCheckboxComponent,
        ButtonComponent,
        HeightSliderComponent,
    ]
})
export class AllFiltersComponent extends AllFiltersBase implements OnInit, OnDestroy {

    @Input() set allFilters(filters: FilterGroup[]) {
        this._allFilters = cloneDeep(filters);
    }
    @Input() doneLabel: string;
    @Input() showMoreLabel: string;
    @Input() useHeightFilter = false;
    @Input() heightLabel: string;

    isSafari = false;

    // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
    get allFilters(): FilterGroup[] {
        return this._allFilters;
    }

    get enableHeightFilter() {
        return this.useHeightFilter && this.heightSliderService.initialized;
    }

    constructor(
        @Inject(DETECT_BROWSER) private detectBrowser: DetectBrowser,
        public filterEmitterService: FilterEmitterService,
        private heightSliderService: HeightSliderService,
        private modalService: ModalService,
        private winRef: WindowRef
    ) {
        super(filterEmitterService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.mapFacetLimit();

        /* istanbul ignore if */
        if (this.detectBrowser(this.winRef, ['Safari'])) {
            this.isSafari = true;
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private mapFacetLimit() {
        this._allFilters = this._allFilters.map((filterGroup) => {
            // calculate the number of facets of all groups
            const facetCount = sumBy(filterGroup.facetGroups, 'facets.length');

            // if the number of facets is higher than the display limit declared in the config
            if (filterGroup.displayLimit > 0 && facetCount > filterGroup.displayLimit) {
                filterGroup = this.reduceFacetsToShow(filterGroup);
            }

            return filterGroup;
        });
    }

    /**
     * Create Modal and build a new facets array based on the maximum of facets declared
     * in the config in a provided filter group
     *
     * @param filterGroup which contains the facets number to reduce
     * @returns which contains the reduced facets to show
     */
    private reduceFacetsToShow(filterGroup: FilterGroup): FilterGroup {
        // create modal
        filterGroup.showMoreModal = this.modalService.create(
            FilterGroupModalComponent,
            {
                title: filterGroup.label,
                data: {
                    facetGroups: cloneDeep(filterGroup.facetGroups),
                    doneLabel: this.doneLabel
                }
            }
        );

        filterGroup.facetGroups = filterGroup.facetGroups.reduce((result, facetGroup) => {
            // calculate the available space before adding
            const availableSpace = Math.max(0, filterGroup.displayLimit - sumBy(result, 'facets.length'));
            // cancel if there is not any available space
            if (availableSpace === 0) {
                return result;
            }
            // if the number of facets of the current group is higher than the available space
            if (availableSpace < facetGroup.facets.length) {
                // remove facets that exceed the available space
                facetGroup.facets.splice(availableSpace, facetGroup.facets.length);
            }

            result.push(facetGroup);

            return result;
        }, []);

        return filterGroup;
    }
}
